import { CalendarOutlined, CopyOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Input, Modal, Popconfirm, Space, Spin, Typography, message } from "antd"
import type { InputRef } from "antd"
import React, { FC, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import { useIframeOverlayTop } from "../../helpers/iframeOverlay"
import { useGetMyCalendarFeedUrlMutation, useResetMyCalendarFeedTokenMutation } from "../../generated/graphql"

const { Text, Paragraph } = Typography

type CalendarFeedModalProps = {
    open: boolean
    onClose: () => void
}

/** webcal:// öffnet die Feed-URL direkt in der Kalender-App (iOS/macOS/Outlook). */
const toWebcal = (url: string): string => url.replace(/^https?:\/\//, "webcal://")

/**
 * Abo-Dialog für den iCal-Kalender-Feed. Beim Öffnen wird die Feed-URL lazy per
 * Mutation erzeugt (getMyCalendarFeedUrl). "Neuen Link erzeugen" invalidiert die
 * alte URL (resetMyCalendarFeedToken).
 */
const CalendarFeedModal: FC<CalendarFeedModalProps> = ({ open, onClose }) => {
    const { t } = useTranslation()
    const inputRef = useRef<InputRef>(null)

    const [loadFeedUrl, { data: getData, loading: getLoading, error: getError }] = useGetMyCalendarFeedUrlMutation()
    const [resetToken, { data: resetData, loading: resetLoading, error: resetError }] = useResetMyCalendarFeedTokenMutation()

    // Im auto-Höhe-iFrame-Embed muss das Modal im sichtbaren Ausschnitt landen
    // (AntD-Default positioniert relativ zum iFrame-Dokument, nicht zum
    // sichtbaren Bereich). Standalone: undefined -> AntD-Default.
    const overlayTop = useIframeOverlayTop(open)

    // Beim Öffnen einmal die Feed-URL laden. reset() hat Vorrang, sobald neu erzeugt.
    useEffect(() => {
        if (open) loadFeedUrl().catch(() => undefined)
    }, [open, loadFeedUrl])

    const feedUrl = resetData?.resetMyCalendarFeedToken ?? getData?.getMyCalendarFeedUrl ?? ""
    const loading = getLoading || resetLoading
    const error = getError || resetError

    const handleCopy = async () => {
        try {
            // navigator.clipboard kann im iFrame fehlen → wirft, Fallback greift
            await navigator.clipboard.writeText(feedUrl)
            message.success(t("account.calendarFeed.copied", "Link kopiert"))
        } catch {
            inputRef.current?.select()
            message.info(t("account.calendarFeed.copyManual", "Bitte manuell kopieren"))
        }
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            style={overlayTop === undefined ? undefined : { top: overlayTop }}
            title={t("account.calendarFeed.title", "Termine im Kalender abonnieren")}
            footer={
                <Button onClick={onClose} data-testid='calendar-feed-close'>
                    {t("account.calendarFeed.close", "Schließen")}
                </Button>
            }
            destroyOnClose
        >
            <div data-testid='calendar-feed-modal'>
                {loading && !feedUrl ? (
                    <div style={{ textAlign: "center", padding: 24 }} data-testid='calendar-feed-loading'>
                        <Spin spinning />
                    </div>
                ) : error && !feedUrl ? (
                    <QueryErrorAlert
                        testId='calendar-feed-error'
                        message={t("account.calendarFeed.loadError", "Die Kalender-Adresse konnte nicht geladen werden.")}
                        onRetry={() => loadFeedUrl()}
                    />
                ) : (
                    <Space direction='vertical' size='middle' style={{ width: "100%" }}>
                        <Button
                            type='primary'
                            icon={<CalendarOutlined />}
                            href={feedUrl ? toWebcal(feedUrl) : undefined}
                            target='_blank'
                            rel='noreferrer'
                            disabled={!feedUrl}
                            data-testid='calendar-feed-webcal'
                        >
                            {t("account.calendarFeed.openInApp", "In Kalender-App öffnen")}
                        </Button>

                        <div>
                            <Text type='secondary'>{t("account.calendarFeed.urlLabel", "Feed-Adresse (URL)")}</Text>
                            <div style={{ display: "flex", gap: 8 }}>
                                <Input ref={inputRef} readOnly value={feedUrl} style={{ flex: 1 }} data-testid='calendar-feed-url' />
                                <Button icon={<CopyOutlined />} onClick={handleCopy} data-testid='calendar-feed-copy'>
                                    {t("account.calendarFeed.copy", "Link kopieren")}
                                </Button>
                            </div>
                        </div>

                        <Paragraph type='secondary' style={{ marginBottom: 0 }}>
                            {t(
                                "account.calendarFeed.googleHint",
                                "Google Kalender (Web): „Weitere Kalender hinzufügen“ → „Per URL“ und diese Adresse einfügen.",
                            )}
                        </Paragraph>
                        <Paragraph type='secondary' style={{ marginBottom: 0 }}>
                            {t(
                                "account.calendarFeed.delayHint",
                                "Änderungen an Terminen erscheinen je nach Kalender-App verzögert (bis zu 24 Stunden). Kurzfristige Absagen erhältst du weiterhin per E-Mail.",
                            )}
                        </Paragraph>

                        <Popconfirm
                            title={t(
                                "account.calendarFeed.resetConfirm",
                                "Die bisherige Feed-Adresse wird ungültig und bestehende Abos brechen ab. Fortfahren?",
                            )}
                            okText={t("account.calendarFeed.reset", "Neuen Link erzeugen")}
                            cancelText={t("account.calendarFeed.cancel", "Abbrechen")}
                            okButtonProps={{ danger: true, loading: resetLoading }}
                            onConfirm={() => resetToken().catch(() => undefined)}
                        >
                            <Button type='link' danger icon={<ReloadOutlined />} data-testid='calendar-feed-reset'>
                                {t("account.calendarFeed.reset", "Neuen Link erzeugen")}
                            </Button>
                        </Popconfirm>
                    </Space>
                )}
            </div>
        </Modal>
    )
}

export default CalendarFeedModal
